import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowRight, Check, Copy, Download, FileText, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';

export type Pedido = {
  id: number;
  fornecedor: string;
  pacote_atual: number;
  pacote_alvo: number;
  desconto_pct: number;
  itens: number;
  com_cobre: boolean;
  texto: string;
  rodada_resposta: { id: number; numero: number; nome: string; aberta_agora: boolean; precos_herdados: number };
};

const reais = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const pct = (v: number) => `${(v * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

/**
 * O pedido de nova proposta, do jeito que o Marcelo manda hoje: um texto para o
 * WhatsApp e a planilha com o alvo de cada item. O texto não cita concorrente —
 * mostra o alvo e quanto está acima.
 *
 * O pedido já chega feito: o clique em "Pedir nova proposta" registra o pedido
 * e abre a próxima rodada de uma vez. Aqui ficam o texto, a planilha e o
 * caminho para as propostas, onde a resposta do fornecedor vai entrar.
 */
export default function PedidoRevisao({
  aberto,
  aoFechar,
  cotacaoId,
  fornecedor,
  carregando,
  erro,
  pedido,
  alvo,
  falta,
  faltaPct,
  itens,
  destino,
}: {
  aberto: boolean;
  aoFechar: () => void;
  cotacaoId: number;
  fornecedor: string;
  carregando: boolean;
  erro: string | null;
  pedido: Pedido | null;
  alvo: number;
  falta: number;
  faltaPct: number;
  itens: number;
  destino: { abre_rodada: boolean; nome: string };
}) {
  const navigate = useNavigate();
  const [copiado, setCopiado] = useState(false);

  const baixarPlanilha = async () => {
    if (!pedido) return;
    const resposta = await api.get(`/pedidos-revisao/${pedido.id}/planilha`, { responseType: 'blob' });
    const url = URL.createObjectURL(resposta.data as Blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pedido-revisao-${fornecedor.toLowerCase()}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const copiar = async () => {
    if (!pedido) return;
    await navigator.clipboard.writeText(pedido.texto);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2500);
  };

  const rodada = pedido?.rodada_resposta.nome ?? destino.nome;

  return (
    <Sheet open={aberto} onOpenChange={(abrir) => !abrir && !carregando && aoFechar()}>
      <SheetContent side="right" className="w-full gap-0 overflow-y-auto p-0 data-[side=right]:sm:max-w-[576px]">
        <SheetHeader className="border-b border-border bg-faixa px-lg py-md text-left">
          <SheetTitle className="text-[16px] font-bold text-white">Nova proposta da {fornecedor}</SheetTitle>
          <SheetDescription className="text-[12px] text-white/70">
            O texto mostra o alvo e quanto cada item está acima dele. Não cita outros fornecedores.
          </SheetDescription>
        </SheetHeader>

        <div className="px-lg py-md">
          <dl className="grid grid-cols-3 gap-sm rounded-md border border-border bg-dominant p-sm text-center">
            <div>
              <dt className="text-[10px] uppercase tracking-wide text-muted">Alvo</dt>
              <dd className="text-[14px] font-bold tabular-nums text-accent">{reais.format(alvo)}</dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-wide text-muted">Falta</dt>
              <dd className="text-[14px] font-bold tabular-nums text-ink">{pct(faltaPct)}</dd>
              <dd className="text-[11px] tabular-nums text-muted">{reais.format(falta)}</dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-wide text-muted">Itens no pedido</dt>
              <dd className="text-[14px] font-bold tabular-nums text-ink">{itens}</dd>
            </div>
          </dl>

          {carregando && (
            <div className="mt-lg flex flex-col items-center gap-sm py-lg text-center">
              <Loader2 size={28} className="animate-spin text-accent" />
              <p className="text-[14px] font-bold text-ink">
                {destino.abre_rodada ? `Registrando o pedido e abrindo a ${destino.nome.toLowerCase()}…` : 'Registrando o pedido…'}
              </p>
              {destino.abre_rodada && (
                <p className="max-w-[384px] text-[12px] text-muted">
                  A rodada nova começa com os preços atuais dos três fornecedores. Leva alguns segundos.
                </p>
              )}
            </div>
          )}

          {erro && (
            <p className="mt-md flex items-start gap-xs rounded-md border border-danger/30 bg-danger/5 px-sm py-xs text-[12px] text-danger">
              <AlertCircle size={14} className="mt-[1px] shrink-0" />
              {erro}
            </p>
          )}

          {pedido && (
            <>
              <div className="mt-md rounded-md border border-success/30 bg-success/5 px-sm py-sm text-[12px] text-ink">
                <p className="flex items-center gap-xs font-bold text-success">
                  <Check size={14} />
                  Pedido registrado{pedido.com_cobre ? ', com o argumento do cobre' : ''}.
                </p>
                <p className="mt-[2px]">
                  {pedido.rodada_resposta.aberta_agora
                    ? `A ${rodada.toLowerCase()} foi aberta com os preços atuais — a nova proposta da ${fornecedor} entra nela.`
                    : `A nova proposta da ${fornecedor} entra na ${rodada.toLowerCase()}, que já está aberta.`}
                </p>
              </div>

              <label className="mt-md block text-[12px] font-bold text-ink" htmlFor="texto-pedido">
                Texto para o WhatsApp ou o e-mail
              </label>
              <textarea
                id="texto-pedido"
                readOnly
                value={pedido.texto}
                rows={16}
                className="mt-xs w-full resize-y rounded-md border border-border bg-surface px-sm py-sm font-mono text-[12px] leading-relaxed text-ink"
              />

              <div className="mt-sm grid grid-cols-2 gap-sm">
                <button
                  type="button"
                  onClick={copiar}
                  className="inline-flex items-center justify-center gap-xs rounded-md bg-faixa px-md py-sm text-[13px] font-bold text-white"
                >
                  {copiado ? <Check size={15} /> : <Copy size={15} />}
                  {copiado ? 'Copiado' : 'Copiar texto'}
                </button>
                <button
                  type="button"
                  onClick={baixarPlanilha}
                  className="inline-flex items-center justify-center gap-xs rounded-md border border-border bg-surface px-md py-sm text-[13px] font-bold text-ink hover:border-accent"
                >
                  <Download size={15} />
                  Baixar planilha
                </button>
              </div>
              <p className="mt-xs flex items-center gap-xs text-[11px] text-muted">
                <FileText size={12} aria-hidden="true" />
                A planilha é a de sempre, com o preço atual e o alvo. O fornecedor preenche o novo preço e ela volta pela
                tela de propostas.
              </p>

              <div className="mt-lg border-t border-border pt-md">
                <button
                  type="button"
                  onClick={() => navigate(`/cotacoes/${cotacaoId}/propostas`)}
                  className="inline-flex w-full items-center justify-center gap-xs rounded-md bg-accent px-md py-sm text-[13px] font-bold text-white hover:bg-accent/90"
                >
                  Ir às propostas da {rodada.toLowerCase()}
                  <ArrowRight size={15} />
                </button>
                <p className="mt-xs text-center text-[11px] text-muted">
                  Para pedir a outro fornecedor também, feche este painel e escolha o fornecedor em "Onde negociar".
                </p>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
